import React, { useState } from 'react';
import { Container, Card, Form, Button, Spinner, Alert } from 'react-bootstrap';
import jsPDF from 'jspdf';

// O funcție helper pentru a prelua o imagine și a o converti în formatul Base64
const fetchImageAsBase64 = (url) => {
  return fetch(url)
    .then(response => response.blob())
    .then(blob => new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    }));
};


const HtmlToPdfConverter = () => {
  const [htmlContent, setHtmlContent] = useState('');
  const [fileName, setFileName] = useState('');
  const [isConverting, setIsConverting] = useState(false);
  const [error, setError] = useState('');

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type === "text/html") {
      const reader = new FileReader();
      reader.onload = (e) => setHtmlContent(e.target.result);
      reader.readAsText(file);
      setFileName(file.name.replace(/\.[^/.]+$/, ""));
      setError('');
    } else {
      setHtmlContent('');
      setFileName('');
      setError('Vă rugăm să selectați un fișier valid în format .html');
    }
  };

  const handleConvertToPdf = async () => {
    if (!htmlContent) {
      setError('Nu există conținut HTML de convertit.');
      return;
    }

    setIsConverting(true);
    setError('');

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, "text/html");

      const rowsData = Array.from(doc.querySelectorAll('.row')).map(row => {
        return Array.from(row.querySelectorAll('.qr-block')).map(block => ({
          qrSrc: block.querySelector('img')?.getAttribute('src'),
          label: block.querySelector('.label')?.innerText.trim(),
        }));
      });

      const imageUrls = [...new Set(rowsData.flat().map(item => item.qrSrc).filter(Boolean))];
      const imagePromises = imageUrls.map(url => fetchImageAsBase64(url));
      const base64Images = await Promise.all(imagePromises);
      
      const imageMap = imageUrls.reduce((map, url, index) => {
        map[url] = base64Images[index];
        return map;
      }, {});
      
      const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });
      
      // --- MODIFICĂRI DE FINEȚE AICI ---
      const MARGIN = 15;
      const PAGE_WIDTH = pdf.internal.pageSize.getWidth();
      const CONTENT_WIDTH = PAGE_WIDTH - (MARGIN * 2);
      const QR_SIZE = 30;
      const LABEL_FONT_SIZE = 8;
      const SPACE_BETWEEN_QR_AND_LABEL = 5; // Spațiu vertical între QR și etichetă
      
      // Am mărit considerabil spațiul dintre rânduri pentru a acomoda textul
      const ROW_VERTICAL_SPACING = 50; 

      let currentY = MARGIN;

      for (const row of rowsData) {
        const itemCount = row.length;
        if (itemCount === 0) continue;
        
        const totalQrWidth = itemCount * QR_SIZE;
        const totalSpacing = CONTENT_WIDTH - totalQrWidth;
        const gap = itemCount > 1 ? totalSpacing / (itemCount - 1) : 0;
        
        let currentX = MARGIN;

        for (const item of row) {
          const imgData = imageMap[item.qrSrc];
          if(imgData) {
            pdf.addImage(imgData, 'PNG', currentX, currentY, QR_SIZE, 0);
          }
          
          pdf.setFontSize(LABEL_FONT_SIZE);
          const labelLines = pdf.splitTextToSize(item.label, QR_SIZE);
          pdf.text(labelLines, currentX + (QR_SIZE / 2), currentY + QR_SIZE + SPACE_BETWEEN_QR_AND_LABEL, { align: 'center' });

          currentX += QR_SIZE + gap;
        }

        currentY += ROW_VERTICAL_SPACING;
      }

      pdf.save(`${fileName || 'document'}.pdf`);

    } catch (err) {
      console.error("A apărut o eroare în procesul de conversie nativă:", err);
      setError("Eroare la conversie. Verificați structura HTML și consola.");
    } finally {
      setIsConverting(false);
    }
  };


  return (
    <Container className="py-5">
      <Card className="shadow-lg">
        <Card.Header as="h4" className="fw-bold text-center">
          Convertor HTML în PDF
        </Card.Header>
        <Card.Body className="p-4">
          {error && <Alert variant="danger">{error}</Alert>}
          <Form.Group controlId="formFile" className="mb-3">
            <Form.Label>1. Alegeți fișierul HTML</Form.Label>
            <Form.Control type="file" accept=".html" onChange={handleFileChange} />
          </Form.Group>
          {htmlContent && <Alert variant="info">Fișier selectat: <strong>{fileName}.html</strong></Alert>}
          <div className="d-grid mt-4">
            <Button variant="primary" size="lg" onClick={handleConvertToPdf} disabled={!htmlContent || isConverting}>
              {isConverting ? (
                <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /><span className="ms-2">Se convertește...</span></>
              ) : '2. Convertește în PDF'}
            </Button>
          </div>
        </Card.Body>
        <Card.Footer className="text-muted text-center">
            Noua versiune citește structura HTML și reconstruiește PDF-ul nativ.
        </Card.Footer>
      </Card>
      
      <div id="html-render-content" style={{ display: 'none' }} />
    </Container>
  );
};

export default HtmlToPdfConverter;
